<template>
  <div class="subscription-view">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="header-flex">
              <div class="header-left">
                <v-btn
                  icon
                  @click="$router.go(-1)"
                  class="mr-3"
                  color="primary"
                >
                  <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <div class="title-section">
                  <h1 class="page-title">
                    <v-icon large class="vibrant-title-icon mr-3">mdi-eye</v-icon>
                    {{ subscription.name || 'Subscription Plan' }}
                  </h1>
                  <p class="page-subtitle">View subscription plan details</p>
                </div>
              </div>
              <div class="header-actions">
                <v-btn
                  color="primary"
                  :to="`/subscriptions/${$route.params.id}/edit`"
                  class="modern-btn"
                  elevation="2"
                >
                  <v-icon left>mdi-pencil</v-icon>
                  Edit Plan
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p>Loading subscription details...</p>
      </div>

      <!-- Subscription Details -->
      <div v-else>
        <v-row>
          <!-- Main Details Card -->
          <v-col cols="12" lg="8">
            <v-card class="subscription-details-card" elevation="8">
              <div class="subscription-header" :style="`background: ${subscription.color || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}`">
                <div class="plan-badge" v-if="subscription.is_popular">
                  <v-icon small>mdi-star</v-icon>
                  Popular
                </div>
                <div class="plan-status-badge">
                  <v-chip
                    :color="subscription.is_active ? 'success' : 'error'"
                    dark
                    small
                  >
                    {{ subscription.is_active ? 'Active' : 'Inactive' }}
                  </v-chip>
                </div>
                <div class="plan-name">{{ subscription.name }}</div>
                <div class="plan-type">{{ formatPlanType(subscription.type) }}</div>
                <div class="plan-description">{{ subscription.description }}</div>
                <div class="plan-pricing">
                  <div class="price-display">
                    <span class="currency">$</span>
                    <span class="price">{{ subscription.monthly_price || 0 }}</span>
                    <span class="period">/month</span>
                  </div>
                  <div class="annual-price" v-if="subscription.annual_price">
                    ${{ subscription.annual_price }}/year
                  </div>
                  <div class="setup-fee" v-if="subscription.setup_fee">
                    Setup fee: ${{ subscription.setup_fee }}
                  </div>
                </div>
              </div>

              <v-card-text class="pa-6">
                <!-- Basic Information -->
                <div class="info-section mb-6">
                  <h3 class="section-title">
                    <v-icon class="mr-2">mdi-information</v-icon>
                    Basic Information
                  </h3>
                  <div class="info-grid">
                    <div class="info-item">
                      <span class="info-label">Plan Type</span>
                      <span class="info-value">{{ formatPlanType(subscription.type) }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Support Level</span>
                      <span class="info-value">{{ formatSupportLevel(subscription.support_level) }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Created</span>
                      <span class="info-value">{{ formatDate(subscription.created_at) }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Status</span>
                      <span class="info-value">
                        <v-chip
                          :color="subscription.is_active ? 'success' : 'error'"
                          dark
                          small
                        >
                          {{ subscription.is_active ? 'Active' : 'Inactive' }}
                        </v-chip>
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Usage Limits -->
                <div class="info-section mb-6">
                  <h3 class="section-title">
                    <v-icon class="mr-2">mdi-speedometer</v-icon>
                    Usage Limits
                  </h3>
                  <div class="info-grid">
                    <div class="info-item">
                      <span class="info-label">Max Users</span>
                      <span class="info-value">{{ subscription.max_users || 'Unlimited' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Max Projects</span>
                      <span class="info-value">{{ subscription.max_projects || 'Unlimited' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Max Storage</span>
                      <span class="info-value">{{ subscription.max_storage_gb ? `${subscription.max_storage_gb} GB` : 'Unlimited' }}</span>
                    </div>
                  </div>
                </div>

                <!-- AI Agents -->
                <div class="info-section mb-6" v-if="subscription.agents && subscription.agents.length">
                  <h3 class="section-title">
                    <v-icon class="mr-2">mdi-robot</v-icon>
                    Included AI Agents ({{ subscription.agents.length }})
                  </h3>
                  <div class="agents-list">
                    <div
                      v-for="agentPlan in subscription.agents"
                      :key="agentPlan.id"
                      class="agent-card"
                    >
                      <div class="agent-info">
                        <v-avatar size="40" class="mr-3">
                          <v-img
                            :src="agentPlan.ai_agent?.logo_url || require('../../assets/sapp-only-logo.png')"
                            :alt="agentPlan.ai_agent?.name"
                          />
                        </v-avatar>
                        <div class="agent-details">
                          <div class="agent-name">{{ agentPlan.ai_agent?.name }}</div>
                          <div class="agent-description">{{ agentPlan.ai_agent?.description }}</div>
                        </div>
                      </div>
                      <div class="agent-credits">
                        <div class="credits-info">
                          <span class="credits-label">Credits</span>
                          <span class="credits-value">{{ agentPlan.included_credits || 'Unlimited' }}</span>
                        </div>
                        <div class="overage-info" v-if="agentPlan.overage_rate">
                          <span class="overage-label">Overage</span>
                          <span class="overage-value">${{ agentPlan.overage_rate }}/credit</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Features -->
                <div class="info-section" v-if="subscription.features && subscription.features.length">
                  <h3 class="section-title">
                    <v-icon class="mr-2">mdi-check-circle</v-icon>
                    Features ({{ subscription.features.length }})
                  </h3>
                  <div class="features-list">
                    <div
                      v-for="feature in subscription.features"
                      :key="feature.id"
                      class="feature-item"
                      :class="{ 'feature-inactive': !feature.is_active }"
                    >
                      <v-icon 
                        :color="feature.is_active ? 'success' : 'grey'"
                        class="mr-3"
                      >
                        {{ feature.is_active ? 'mdi-check-circle' : 'mdi-minus-circle' }}
                      </v-icon>
                      <div class="feature-content">
                        <div class="feature-name">{{ feature.name }}</div>
                        <div class="feature-description" v-if="feature.description">
                          {{ feature.description }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Sidebar -->
          <v-col cols="12" lg="4">
            <!-- Quick Actions -->
            <v-card class="sidebar-card mb-4" elevation="4">
              <v-card-title class="sidebar-header">
                <v-icon class="mr-2">mdi-lightning-bolt</v-icon>
                Quick Actions
              </v-card-title>
              <v-card-text class="pa-4">
                <div class="action-buttons">
                  <v-btn
                    block
                    color="primary"
                    :to="`/subscriptions/${$route.params.id}/edit`"
                    class="mb-3"
                  >
                    <v-icon left>mdi-pencil</v-icon>
                    Edit Plan
                  </v-btn>
                  <v-btn
                    block
                    outlined
                    color="success"
                    @click="assignToCompany"
                    class="mb-3"
                  >
                    <v-icon left>mdi-account-plus</v-icon>
                    Assign to Company
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <!-- Statistics -->
            <v-card class="sidebar-card" elevation="4">
              <v-card-title class="sidebar-header">
                <v-icon class="mr-2">mdi-chart-bar</v-icon>
                Statistics
              </v-card-title>
              <v-card-text class="pa-4">
                <div class="stats-list">
                  <div class="stat-item">
                    <span class="stat-label">Total Agents</span>
                    <span class="stat-value">{{ subscription.agents?.length || 0 }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Total Features</span>
                    <span class="stat-value">{{ subscription.features?.length || 0 }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Active Features</span>
                    <span class="stat-value">{{ activeFeatures }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Annual Savings</span>
                    <span class="stat-value">${{ annualSavings }}</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-container>

    <!-- Assign to Company Dialog -->
    <v-dialog v-model="showAssignDialog" max-width="800px" persistent>
      <v-card class="assign-dialog" elevation="24">
        <v-card-title class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-title-section">
              <h2 class="dialog-title">Assign Subscription</h2>
              <p class="dialog-subtitle">Assign "{{ subscription.name }}" to a company</p>
            </div>
            <v-btn 
              icon 
              @click="closeAssignDialog"
              class="close-btn"
            >
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content pa-6">
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="assignForm.company_id"
                :items="companies"
                outlined
                label="Select Company *"
                prepend-inner-icon="mdi-office-building"
                :error-messages="assignErrors.company_id"
                class="minimal-input"
                dense
                hide-details="auto"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="assignForm.start_date"
                outlined
                label="Start Date *"
                prepend-inner-icon="mdi-calendar"
                :error-messages="assignErrors.start_date"
                class="minimal-input"
                dense
                type="date"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="assignForm.end_date"
                outlined
                label="End Date"
                prepend-inner-icon="mdi-calendar"
                :error-messages="assignErrors.end_date"
                class="minimal-input"
                dense
                type="date"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="assignForm.coupon_code"
                :items="coupons"
                outlined
                label="Apply Coupon (Optional)"
                prepend-inner-icon="mdi-tag"
                class="minimal-input"
                dense
                clearable
                hide-details="auto"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <v-switch
                v-model="assignForm.is_custom"
                inset
                color="primary"
                label="Custom Subscription"
                class="minimal-switch"
                hide-details
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Custom Agent Selection -->
          <div v-if="assignForm.is_custom" class="custom-agents-section mt-4">
            <h4 class="section-title mb-3">
              <v-icon small class="mr-2">mdi-robot-excited</v-icon>
              Select AI Agents
            </h4>
            <div class="agents-grid">
              <div
                v-for="agent in availableAgents"
                :key="agent.id"
                class="agent-select-card"
                :class="{ 'selected': assignForm.selected_agents.includes(agent.id) }"
                @click="toggleAgent(agent.id)"
              >
                <v-checkbox
                  :value="assignForm.selected_agents.includes(agent.id)"
                  @click.stop="toggleAgent(agent.id)"
                  color="primary"
                  hide-details
                  class="agent-checkbox"
                ></v-checkbox>
                <v-avatar size="32" class="mb-2">
                  <v-img
                    :src="agent.logo_url || require('../../assets/sapp-only-logo.png')"
                    :alt="agent.name"
                  />
                </v-avatar>
                <div class="agent-name">{{ agent.name }}</div>
                <div class="agent-price">${{ agent.monthly_price || 0 }}/mo</div>
              </div>
            </div>
            <div class="custom-total mt-3">
              <strong>Total Cost: ${{ calculateCustomTotal() }}/month</strong>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="dialog-actions pa-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeAssignDialog"
            class="cancel-btn"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="assignSubscription"
            :loading="isAssigning"
            class="save-btn"
          >
            Assign Subscription
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "SubscriptionView",
  data() {
    return {
      subscription: {},
      isLoading: false,
      subscriptionId: null,
      showAssignDialog: false,
      isAssigning: false,
      companies: [],
      coupons: [],
      availableAgents: [],
      assignForm: {
        company_id: null,
        start_date: new Date().toISOString().substr(0, 10),
        end_date: null,
        coupon_code: null,
        is_custom: false,
        selected_agents: [],
      },
      assignErrors: {},
    };
  },
  computed: {
    activeFeatures() {
      if (!this.subscription.features) return 0;
      return this.subscription.features.filter(f => f.is_active).length;
    },
    annualSavings() {
      if (!this.subscription.monthly_price || !this.subscription.annual_price) return 0;
      const monthlyTotal = this.subscription.monthly_price * 12;
      return Math.max(0, monthlyTotal - this.subscription.annual_price);
    }
  },
  mounted() {
    this.loadSubscription();
    this.getCompanies();
    this.getCoupons();
    this.getAvailableAgents();
  },
  methods: {
    async loadSubscription() {
      this.isLoading = true;
      try {
        // Use the encrypted ID directly - backend will decrypt it
        const response = await axios.get(`/subscription-plans/${this.$route.params.id}`);
        this.subscription = response.data.data;
      } catch (error) {
        console.error('Error loading subscription:', error);
        // Load demo data for testing
        this.subscription = {
          id: 1,
          name: 'Professional Plan',
          description: 'Ideal for growing businesses with advanced needs',
          type: 'professional',
          monthly_price: 299,
          annual_price: 2990,
          setup_fee: 99,
          max_users: 25,
          max_projects: 15,
          max_storage_gb: 100,
          support_level: 'priority',
          is_popular: true,
          is_active: true,
          created_at: '2025-06-13T10:00:00Z',
          color: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          agents: [
            {
              id: 1,
              ai_agent: {
                id: 1,
                name: 'ContentMod AI',
                description: 'Advanced AI agent for content moderation with real-time analysis and automated decision making.',
                logo_url: null
              },
              included_credits: 50000,
              overage_rate: 0.005
            }
          ],
          features: [
            { id: 1, name: 'Advanced ContentMod AI', description: 'Full content moderation capabilities', is_active: true },
            { id: 2, name: 'Priority Support', description: 'Priority email and chat support', is_active: true },
            { id: 3, name: 'Advanced Analytics', description: 'Detailed moderation analytics and reporting', is_active: true },
            { id: 4, name: 'Enhanced API Access', description: '50,000 API calls/month', is_active: true },
            { id: 5, name: 'Custom Rules', description: 'Advanced custom moderation rules', is_active: true },
            { id: 6, name: 'Webhooks', description: 'Real-time moderation notifications', is_active: true }
          ]
        };
      }
      this.isLoading = false;
    },
    formatPlanType(type) {
      if (!type) return '';
      return type.charAt(0).toUpperCase() + type.slice(1);
    },
    formatSupportLevel(level) {
      const levels = {
        email: 'Email Support',
        priority: 'Priority Support',
        dedicated: 'Dedicated Support'
      };
      return levels[level] || level;
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString();
    },
    async getCompanies() {
      try {
        const response = await axios.get('/companies');
        this.companies = response.data.data.map(company => ({
          text: company.name,
          value: company.id
        }));
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    },
    async getCoupons() {
      try {
        const response = await axios.get('/coupons');
        this.coupons = response.data.data.map(coupon => ({
          text: `${coupon.code} (${coupon.type === 'percent' ? coupon.value + '%' : '$' + coupon.value} off)`,
          value: coupon.code
        }));
      } catch (error) {
        console.error('Error fetching coupons:', error);
        // Fallback to sample coupon data
        this.coupons = [
          { text: 'WELCOME20 (20% off)', value: 'WELCOME20' },
          { text: 'SAVE50 ($50 off)', value: 'SAVE50' },
          { text: 'FIRST30 (30% off)', value: 'FIRST30' },
          { text: 'STUDENT15 (15% off)', value: 'STUDENT15' },
          { text: 'ENTERPRISE100 ($100 off)', value: 'ENTERPRISE100' },
          { text: 'EARLY25 (25% off)', value: 'EARLY25' },
        ];
      }
    },
    async getAvailableAgents() {
      try {
        const response = await axios.get('/ai-agents?is_active=true');
        this.availableAgents = response.data.data;
      } catch (error) {
        console.error('Error fetching agents:', error);
        // Show empty state if no agents from API
        this.availableAgents = [];
      }
    },
    assignToCompany() {
      this.assignForm = {
        company_id: null,
        start_date: new Date().toISOString().substr(0, 10),
        end_date: null,
        coupon_code: null,
        is_custom: false,
        selected_agents: this.subscription.agents?.map(agent => agent.ai_agent_id) || [],
      };
      this.assignErrors = {};
      this.showAssignDialog = true;
    },
    closeAssignDialog() {
      this.showAssignDialog = false;
      this.assignForm = {
        company_id: null,
        start_date: new Date().toISOString().substr(0, 10),
        end_date: null,
        coupon_code: null,
        is_custom: false,
        selected_agents: [],
      };
      this.assignErrors = {};
    },
    toggleAgent(agentId) {
      const index = this.assignForm.selected_agents.indexOf(agentId);
      if (index > -1) {
        this.assignForm.selected_agents.splice(index, 1);
      } else {
        this.assignForm.selected_agents.push(agentId);
      }
    },
    calculateCustomTotal() {
      return this.assignForm.selected_agents.reduce((total, agentId) => {
        const agent = this.availableAgents.find(a => a.id === agentId);
        return total + (agent?.monthly_price || 0);
      }, 0);
    },
    async assignSubscription() {
      try {
        this.isAssigning = true;
        this.assignErrors = {};
        
        const payload = {
          subscription_id: this.subscription.id,
          ...this.assignForm,
        };

        await axios.post('/company-subscriptions', payload);
        this.closeAssignDialog();
        // Show success message or redirect
        this.$router.push('/active-subscriptions');
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
          this.assignErrors = error.response.data.errors;
        }
        console.error('Error assigning subscription:', error);
      }
      this.isAssigning = false;
    },
  }
};
</script>

<style scoped>
.subscription-view {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.header-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.title-section {
  flex: 1;
  min-width: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  font-weight: 400;
  margin: 0;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4));
  font-size: 32px !important;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  padding: 0 20px !important;
  height: 44px !important;
  font-size: 14px !important;
  transition: all 0.3s ease;
}

.modern-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

/* Subscription Details Card */
.subscription-details-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  overflow: hidden;
}

.subscription-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 32px;
  position: relative;
}

.plan-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  padding: 6px 16px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}

.plan-status-badge {
  position: absolute;
  top: 16px;
  left: 16px;
}

.plan-name {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 4px;
  margin-top: 20px;
}

.plan-type {
  font-size: 14px;
  opacity: 0.8;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.plan-description {
  font-size: 16px;
  opacity: 0.9;
  margin-bottom: 24px;
  line-height: 1.5;
}

.plan-pricing {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.price-display {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 8px;
}

.currency {
  font-size: 24px;
  font-weight: 600;
}

.price {
  font-size: 36px;
  font-weight: 700;
}

.period {
  font-size: 18px;
  opacity: 0.8;
}

.annual-price,
.setup-fee {
  font-size: 14px;
  opacity: 0.8;
  margin-bottom: 4px;
}

/* Info Sections */
.info-section {
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 24px;
}

.info-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.info-value {
  font-size: 16px;
  color: #2c3e50;
  font-weight: 600;
}

/* Agents List */
.agents-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.agent-card {
  background: #f8f9fa;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.agent-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.agent-details {
  flex: 1;
}

.agent-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.agent-description {
  font-size: 14px;
  color: #64748b;
}

.agent-credits {
  text-align: right;
}

.credits-info,
.overage-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 4px;
}

.credits-label,
.overage-label {
  font-size: 12px;
  color: #64748b;
}

.credits-value,
.overage-value {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
}

/* Features List */
.features-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.feature-item.feature-inactive {
  opacity: 0.6;
  background: #f1f5f9;
}

.feature-content {
  flex: 1;
}

.feature-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.feature-description {
  font-size: 14px;
  color: #64748b;
}

/* Sidebar */
.sidebar-card {
  border-radius: 12px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.sidebar-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  padding: 16px 20px !important;
  font-size: 16px !important;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-buttons .v-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  height: auto !important;
  padding: 12px 16px !important;
  line-height: 1.4 !important;
}

.stats-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.stat-value {
  font-size: 16px;
  color: #2c3e50;
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 960px) {
  .header-flex {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .title-section {
    text-align: left;
  }
  
  .page-title {
    font-size: 22px;
    flex-wrap: wrap;
  }
  
  .header-actions {
    justify-content: flex-start;
    width: 100%;
  }
  
  .modern-btn {
    width: 100%;
    justify-content: center;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .agent-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .agent-credits {
    text-align: left;
    width: 100%;
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 20px;
  }
  
  .page-title .vibrant-title-icon {
    font-size: 24px !important;
  }
  
  .subscription-header {
    padding: 20px 16px;
  }
  
  .plan-pricing .price-display {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .modern-btn {
    font-size: 13px !important;
    padding: 0 16px !important;
    height: 40px !important;
  }
}

/* Responsive for smaller screens */
@media (max-width: 480px) {
  .header-left {
    flex-direction: row;
    align-items: center;
  }
  
  .page-title {
    font-size: 18px;
  }
  
  .page-title .vibrant-title-icon {
    font-size: 20px !important;
    margin-right: 8px;
  }
  
  .modern-btn {
    font-size: 12px !important;
    padding: 0 12px !important;
    height: 36px !important;
  }
  
  .subscription-header {
    padding: 16px 12px;
  }
  
  .plan-name {
    font-size: 24px;
  }
  
  .plan-description {
    font-size: 14px;
  }
}

/* Assign Dialog Styling */
.assign-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  padding: 24px !important;
  border-radius: 0 !important;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.dialog-title-section {
  flex: 1;
}

.dialog-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}

.dialog-subtitle {
  margin: 4px 0 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.dialog-content {
  background: #f8f9fa;
}

/* Custom Agents Section */
.custom-agents-section {
  background: white;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e2e8f0;
}

.agents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.agent-select-card {
  background: #f8f9fa;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.agent-select-card:hover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.05);
}

.agent-select-card.selected {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.1);
}

.agent-checkbox {
  position: absolute;
  top: 8px;
  right: 8px;
}

.agent-select-card .agent-name {
  font-size: 12px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.agent-select-card .agent-price {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.custom-total {
  text-align: center;
  font-size: 16px;
  color: #2c3e50;
  background: #e8f4fd;
  border-radius: 8px;
  padding: 12px;
  border: 2px solid #667eea;
}

/* Minimal Input Styling */
.minimal-input {
  margin-bottom: 8px;
}

.minimal-input >>> .v-input__control {
  min-height: 56px;
}

.minimal-input >>> .v-input__control .v-input__slot {
  background: white !important;
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
  padding: 0 16px !important;
  margin: 0 !important;
  box-shadow: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:before,
.minimal-input >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:hover {
  border-color: #cbd5e1 !important;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #667eea !important;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-label {
  color: #64748b !important;
  font-weight: 500;
  font-size: 14px;
}

.minimal-input >>> .v-label--active {
  color: #667eea !important;
  transform: translateY(-18px) scale(0.85) !important;
}

.minimal-input >>> input {
  color: #1e293b !important;
  font-weight: 500;
  font-size: 15px;
  padding: 8px 0 !important;
}

.minimal-input >>> .v-input__prepend-inner .v-icon {
  color: #667eea !important;
  margin-right: 12px;
}

/* Remove fieldset borders */
.minimal-input >>> fieldset {
  display: none !important;
}

/* Switch Styling */
.minimal-switch >>> .v-input--switch__thumb {
  color: #667eea !important;
}

.minimal-switch >>> .v-input--switch__track {
  color: #e2e8f0 !important;
}

.minimal-switch >>> .v-input--switch.v-input--is-label-active .v-input--switch__track {
  color: #667eea !important;
}

.minimal-switch >>> .v-label {
  color: #2c3e50 !important;
  font-weight: 500;
  font-size: 15px;
}

.dialog-actions {
  background: #f8f9fa;
  border-top: 1px solid rgba(102, 126, 234, 0.1);
}

.cancel-btn {
  color: #64748b !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.cancel-btn:hover {
  background: rgba(100, 116, 139, 0.1) !important;
}

.save-btn {
  background: #667eea !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.save-btn:hover {
  background: #5a6fd8 !important;
}

.save-btn:before {
  background-color: transparent;
}

@media (max-width: 600px) {
  .page-title {
    font-size: 20px;
  }
  
  .subscription-header {
    padding: 24px 20px;
  }
  
  .plan-name {
    font-size: 24px;
  }
  
  .price {
    font-size: 32px;
  }
  
  .agents-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
}
</style>